import React, { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Skeleton, Stack } from '@mui/material'
import { useRouteMatch } from 'react-router-dom'

import { DateFilter } from '@trivie/ui-web/components/date-filter'
import { Insight } from '@trivie/ui-web/components/insight'
import { Mastery } from '@trivie/ui-web/components/mastery'
import { Punchcard } from '@trivie/ui-web/components/punchcard'
import { Text } from '@trivie/ui-web/components/text'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { StrongestWeakest } from '@trivie/ui-web/components/strongest-weakest'
import { Divider } from '@trivie/ui-web/components/divider'
import { useStores } from '@trivie/core'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { GroupApi } from '@trivie/core/src/services/api-objects'

const Overview: React.FunctionComponent = observer(() => {
  const { groupStore } = useStores()
  const {
    groupDetail,
    knowledgeGain,
    knowledgeGap,
    accomplishments,
    hourlyActivity,
    mastery,
    setSelectedGroupId,
    getGroupDetail,
    getGroupAccomplishments,
    getGroupHourlyActivity,
    getGroupKnowledgeGain,
    getGroupKnowledgeGap,
    getGroupMastery,
  } = groupStore

  const [loadingGroupDetail, setLoadingGroupDetail] = useState(true)
  const [loadingAccomplishments, setLoadingAccomplishments] = useState(true)
  const [loadingHourlyActivity, setLoadingHourlyActivity] = useState(true)
  const [loadingKnowledgeGain, setLoadingKnowledgeGain] = useState(true)
  const [loadingKnowledgeGaps, setLoadingKnowledgeGaps] = useState(true)
  const [loadingMastery, setLoadingMastery] = useState(true)

  const gapCancelRef = useRef<any>()
  const detailCancelRef = useRef<any>()
  const accomplishmentsCancelRef = useRef<any>()
  const hourlyActivityCancelRef = useRef<any>()
  const knowledgeGainCancelRef = useRef<any>()
  const masteryCancelRef = useRef<any>()

  const match = useRouteMatch<any>('/people/groups/:id/overview')
  // @ts-ignore
  const { params } = match

  useEffect(() => {
    return () => {
      cancelRequests()
    }
  }, [])

  const cancelRequests = () => {
    if (detailCancelRef.current) {
      detailCancelRef.current.cancel()
    }
    if (accomplishmentsCancelRef.current) {
      accomplishmentsCancelRef.current.cancel()
    }
    if (hourlyActivityCancelRef.current) {
      hourlyActivityCancelRef.current.cancel()
    }

    if (knowledgeGainCancelRef.current) {
      knowledgeGainCancelRef.current.cancel()
    }
    if (masteryCancelRef.current) {
      masteryCancelRef.current.cancel()
    }

    if (gapCancelRef.current) {
      gapCancelRef.current.cancel()
    }
  }

  const load = (start: any, end: any) => {
    setSelectedGroupId(params.id)
    cancelRequests()

    const detailCancelTokenSource = GroupApi.getCancelToken()
    detailCancelRef.current = detailCancelTokenSource
    getGroupDetail(start, end, detailCancelTokenSource.token).then(() =>
      setLoadingGroupDetail(false),
    )

    const accomplishmentsCancelTokenSource = GroupApi.getCancelToken()
    accomplishmentsCancelRef.current = accomplishmentsCancelTokenSource
    getGroupAccomplishments(start, end, accomplishmentsCancelTokenSource.token).then(() =>
      setLoadingAccomplishments(false),
    )

    const hourlyActivityCancelTokenSource = GroupApi.getCancelToken()
    hourlyActivityCancelRef.current = hourlyActivityCancelTokenSource
    getGroupHourlyActivity(start, end, hourlyActivityCancelTokenSource.token).then(() =>
      setLoadingHourlyActivity(false),
    )

    const knowledgeGainCancelTokenSource = GroupApi.getCancelToken()
    knowledgeGainCancelRef.current = knowledgeGainCancelTokenSource
    getGroupKnowledgeGain(start, end, knowledgeGainCancelTokenSource.token).then(() =>
      setLoadingKnowledgeGain(false),
    )

    const masteryCancelTokenSource = GroupApi.getCancelToken()
    masteryCancelRef.current = masteryCancelTokenSource
    getGroupMastery(start, end, masteryCancelTokenSource.token).then(() => setLoadingMastery(false))

    const cancelTokenSource = GroupApi.getCancelToken()
    gapCancelRef.current = cancelTokenSource
    getGroupKnowledgeGap(start, end, cancelTokenSource.token).then(() =>
      setLoadingKnowledgeGaps(false),
    )
  }

  const onDateChange = (start, end, label) => {
    setLoadingGroupDetail(true)
    setLoadingAccomplishments(true)
    setLoadingHourlyActivity(true)
    setLoadingKnowledgeGain(true)
    setLoadingMastery(true)
    setTimespan(label as any)
    load(start, end)
  }

  const [timespan, setTimespan] = useState('All Time')

  return (
    <>
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <DateFilter onDateChange={onDateChange} />
        <GenerateReport filename={groupDetail?.label ?? 'group-detail'} />
      </Stack>
      <Stack useFlexGap gap={2} pb={4}>
        {!loadingKnowledgeGain ? (
          <Panel title="How much is this group learning?">
            <Stack gap={2} direction="row" flexWrap="wrap">
              <Insight
                timespan={timespan}
                unit="%"
                data={knowledgeGain?.stats_history[2].data_points}
                title="Baseline Knowledge"
                value={
                  knowledgeGain
                    ? `${
                        Number.isInteger(knowledgeGain?.starting_knowledge)
                          ? knowledgeGain?.starting_knowledge
                          : knowledgeGain?.starting_knowledge.toFixed(1) ?? 0
                      }%`
                    : '0%'
                }
              />
              <Insight
                timespan={timespan}
                unit="%"
                data={knowledgeGain?.stats_history[1].data_points}
                title="Current Knowledge"
                value={
                  knowledgeGain
                    ? `${
                        Number.isInteger(knowledgeGain?.current_knowledge)
                          ? knowledgeGain?.current_knowledge
                          : knowledgeGain?.current_knowledge.toFixed(1) ?? 0
                      }%`
                    : '0%'
                }
              />
              <Insight
                timespan={timespan}
                unit="%"
                data={knowledgeGain?.stats_history[0].data_points}
                title="Knowledge Gain"
                value={
                  knowledgeGain
                    ? `${
                        Number.isInteger(knowledgeGain?.knowledge_gain)
                          ? knowledgeGain?.knowledge_gain
                          : knowledgeGain?.knowledge_gain.toFixed(1) ?? 0
                      }%`
                    : '0%'
                }
              />
            </Stack>
            <Text
              color="text.secondary"
              variant="body2Medium"
              text="We measure each person’s baseline knowledge by their first answer to each question. As they answer more questions, their current knowledge rises, resulting in knowledge gain."
            />
          </Panel>
        ) : (
          <Box minHeight={185} sx={{ flex: 1, width: '100%' }}>
            <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
          </Box>
        )}
        <Stack useFlexGap gap={2} direction="row" flexWrap="wrap">
          {!loadingGroupDetail ? (
            <>
              <Panel sx={{ flex: 1 }}>
                <Insight
                  timespan={timespan}
                  unit=" Users"
                  outlined
                  title="Active Users"
                  loading={!groupDetail}
                  value={groupDetail?.active_users}
                  data={groupDetail?.stats_history[0].data_points}
                  style={{ marginRight: 20 }}
                />
              </Panel>
              <Panel sx={{ flex: 1 }}>
                <Insight
                  timespan={timespan}
                  unit=""
                  outlined
                  title="Average Mastery"
                  data={groupDetail?.stats_history[2].data_points}
                  value={groupDetail?.mastery.toFixed(1)}
                  ofVal="/ 4"
                  style={{ marginRight: 20 }}
                />
              </Panel>
              <Panel sx={{ flex: 1 }}>
                <Insight
                  timespan={timespan}
                  unit="%"
                  outlined
                  title="Correct Answer %"
                  data={groupDetail?.stats_history[1].data_points}
                  value={`${groupDetail?.pct_correct.toFixed(1) || ''}%`}
                />
              </Panel>
            </>
          ) : (
            <Box minHeight={129} sx={{ flex: 1, width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          )}
        </Stack>
        <Stack flexWrap="wrap" direction="row" useFlexGap gap={2}>
          {loadingKnowledgeGaps ? (
            <Box minHeight={280} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <StrongestWeakest
              type="Weakest"
              variant="medium"
              topics={knowledgeGap?.weakest_topics}
              questions={knowledgeGap?.weakest_questions}
              style={{ flex: 1 }}
            />
          )}
          {loadingKnowledgeGaps ? (
            <Box minHeight={280} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <StrongestWeakest
              type="Strongest"
              variant="medium"
              topics={knowledgeGap?.strongest_topics}
              questions={knowledgeGap?.strongest_questions}
              style={{ flex: 1 }}
            />
          )}
        </Stack>
        <Stack useFlexGap gap={2} flexWrap="wrap" direction="row">
          {!loadingAccomplishments ? (
            <Panel style={{ flex: 1 }} title="What are people in this group accomplishing?">
              <Stack
                divider={<Divider />}
                height="100%"
                justifyContent="center"
                useFlexGap
                spacing={3}
              >
                <Insight
                  timespan={timespan}
                  title="Questions Answered"
                  unit=" Questions"
                  data={accomplishments?.stats_history[0].data_points}
                  value={`${accomplishments?.questions_answered ?? 0}`}
                />
                <Insight
                  timespan={timespan}
                  title="Quizzes Completed"
                  unit=" Quizzes"
                  data={accomplishments?.stats_history[1].data_points}
                  value={`${accomplishments?.quizzes_completed ?? 0}`}
                />
                <Insight
                  timespan={timespan}
                  title="Sources Viewed"
                  unit=" Sources"
                  data={accomplishments?.stats_history[2].data_points}
                  value={`${accomplishments?.resources_viewed ?? 0}`}
                />
              </Stack>
            </Panel>
          ) : (
            <Box sx={{ flex: 1, width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          )}
          {loadingHourlyActivity ? (
            <Box minHeight={503} sx={{ flex: 2, width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel style={{ flex: 2 }} divider title="When are people in this group learning most?">
              <Punchcard values={hourlyActivity} />
            </Panel>
          )}
        </Stack>
        {loadingMastery ? (
          <Box minHeight={157} flex={1} sx={{ width: '100%', height: '100%' }}>
            <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
          </Box>
        ) : (
          <Panel title="Mastery Distribution">
            <Stack gap={3} direction="row" flexWrap="wrap">
              <Mastery
                variant="green"
                percentage={mastery?.most}
                message={`${mastery?.most_user_count} users in this group have mastered most of their questions.`}
              />
              <Mastery
                variant="yellow"
                percentage={mastery?.some}
                message={`${mastery?.some_user_count} users in this group have mastered some of their questions.`}
              />
              <Mastery
                variant="red"
                percentage={mastery?.few}
                message={`${mastery?.few_user_count} users in this group have mastered few of their questions.`}
              />
            </Stack>
          </Panel>
        )}
      </Stack>
      {/* )} */}
    </>
  )
})

export const GroupsOverviewTab = (props: any) => {
  const { id } = props
  return {
    name: 'Overview',
    path: `/people/groups/${id}/overview`,
    component: () => <Overview />,
  }
}
